/**
 * Configuration bundle delivered to a robot (tar.gz downloaded by the agent).
 *
 * Layout chosen to match what the onboard stack already reads, so the robot
 * team can adopt it with minimal change:
 *
 *   manifest.json                 — BundleManifest (this file)
 *   maps/<map_name>.yaml          — Nav2 map_server yaml (image: <map_name>.pgm)
 *   maps/<map_name>.pgm           — occupancy image (unchanged from upload)
 *   maps/<map_name>.json          — fixture detections, same schema as today's
 *                                   ordered_list_service input ({metadata, detections})
 *   routes/route_network.json     — schema_version 1 route network (nodes + dense
 *                                   sampled edges), same as nav2_route_network_planner
 *   platform/waypoints.json       — all platform waypoints with types/properties
 *   platform/paths.json           — control points + spline params + sampled poses
 *   platform/workflows.json       — workflow definitions (steps reference ids above)
 *
 * `manifest.compat` declares what the robot must support (config API level, content
 * schemas, capabilities); the robot checks it while staging (robot-link.ts §7.8).
 */
import { z } from "zod";
export const BUNDLE_SCHEMA_VERSION = 1;
/**
 * Config API level the platform builds bundles for. The robot refuses (at staging, with
 * BUNDLE.INCOMPATIBLE) a bundle whose level, content schema or required capability it
 * does not support. Level 2 = bundles with a `compat` block (activation contract, §7.8).
 */
export const BUNDLE_CONFIG_API = 2;
/** Content schemas inside a bundle and the version this platform writes. */
export const BUNDLE_CONTENT_SCHEMAS = {
    map_yaml: 1,
    fixtures: 1,
    route_network: 1,
    waypoints: 1,
    paths: 1,
    workflows: 1,
};
/**
 * Compatibility requirements declared by the bundle. Absent in bundles built before the
 * activation contract: robots treat that as `{configApi: 1, schemas: BUNDLE_CONTENT_SCHEMAS
 * (all 1), requiresCapabilities: []}`.
 */
export const BundleCompat = z.object({
    configApi: z.number().int().positive(),
    schemas: z.record(z.number().int().positive()),
    requiresCapabilities: z.array(z.string()).default([]),
});
/** The compatibility block the robot assumes for a bundle without one. */
export const LEGACY_BUNDLE_COMPAT = { configApi: 1, schemas: { ...BUNDLE_CONTENT_SCHEMAS }, requiresCapabilities: [] };
export const BundleFileEntry = z.object({
    path: z.string(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    sizeBytes: z.number().int().nonnegative(),
});
export const BundleManifest = z.object({
    schemaVersion: z.literal(BUNDLE_SCHEMA_VERSION),
    deploymentId: z.string().uuid(),
    robotId: z.string().uuid(),
    bundleVersion: z.number().int().positive(),
    createdAt: z.string(),
    map: z.object({
        id: z.string().uuid(),
        name: z.string(),
        version: z.number().int().positive(),
        sha256: z.string(),
    }),
    files: z.array(BundleFileEntry),
    compat: BundleCompat.optional(),
});
//# sourceMappingURL=bundle.js.map